import { useNavigate, useParams, useRouterState } from "@tanstack/react-router";
import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { routeUrl } from "@/app/routes";
import { useAuth } from "@/shared/auth/auth.context";
import { WORKSPACES, type Workspace } from "@/shared/components/layout/sidebar-workspace.constants";
import { useEntitlements } from "@/shared/entitlements/entitlements.context";

type ActiveWorkspaceContextValue = {
  /** The workspace resolved from the URL `$workspaceSlug` param. Falls back
   * to the user's first workspace when the URL has no slug yet (e.g. on
   * `/profile`). For anonymous sessions, returns the first mock workspace. */
  workspace: Workspace;
  /** False when the current URL slug does not exist in the latest auth payload yet. */
  isResolved: boolean;
  /** Every workspace the caller belongs to (from `useAuth().workspaces`). */
  workspaces: ReadonlyArray<Workspace>;
  /** Navigate the URL to the target workspace, preserving the sub-path
   * (e.g. `/old/people` → `/new/people`). */
  setWorkspace: (id: string) => void;
  /** True while a workspace change is in flight or waiting for auth data to resolve it. */
  isSwitching: boolean;
};

function getDefaultMockWorkspace(): Workspace {
  const first = WORKSPACES[0];
  if (!first) {
    throw new Error("WORKSPACES must declare at least one workspace");
  }
  return first;
}

const DEFAULT_MOCK_WORKSPACE = getDefaultMockWorkspace();
const ActiveWorkspaceContext = createContext<ActiveWorkspaceContextValue | null>(null);

export function buildWorkspaceSwitchPath({
  pathname,
  currentSlug,
  targetSlug,
}: {
  pathname: string;
  currentSlug?: string;
  targetSlug: string;
}): string {
  const home = routeUrl.workspace.home(targetSlug);
  return currentSlug ? pathname.replace(/^\/[^/]+/, home) : home;
}

export function shouldFinishWorkspaceSwitch({
  pendingWorkspaceId,
  resolvedWorkspaceId,
  isResolved,
}: {
  pendingWorkspaceId: string | null;
  resolvedWorkspaceId: string;
  isResolved: boolean;
}): boolean {
  return pendingWorkspaceId !== null && isResolved && resolvedWorkspaceId === pendingWorkspaceId;
}

/**
 * Reads the active workspace from the URL (`$workspaceSlug`). The provider
 * shell is preserved for back-compat, the hook itself is URL-driven now, so
 * switching workspaces means navigating to a new path. This keeps deep
 * links honest and the back button useful.
 */
export function ActiveWorkspaceProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  // Entitlements are account-level (one plan per account, see
  // entitlements.context.tsx), so every workspace under the caller's account
  // shares this same plan value.
  const { plan } = useEntitlements();
  const navigate = useNavigate();
  const { location } = useRouterState();
  const params = useParams({ strict: false }) as { workspaceSlug?: string };
  const slugInUrl = params.workspaceSlug;
  const [pendingWorkspaceId, setPendingWorkspaceId] = useState<string | null>(null);

  // Until auth/me lands, `auth.workspaces` is empty and the mock list stands in.
  const isPlaceholder = auth.workspaces.length === 0;

  const workspaces: ReadonlyArray<Workspace> = useMemo(() => {
    if (!isPlaceholder) {
      return auth.workspaces.map((workspace) => ({
        id: workspace.id,
        slug: workspace.slug,
        name: workspace.name,
        isDemo: workspace.isDemo,
        isReadOnly: workspace.isReadOnly,
        mark: workspace.name.slice(0, 1).toUpperCase(),
        markBackground: "var(--pw-pulse)",
        plan,
        memberCount: workspace.memberCount,
        hiringGovernanceEnabled: workspace.hiringGovernanceEnabled,
        operatingDate: workspace.operatingDate ?? null,
      }));
    }
    return WORKSPACES;
  }, [auth.workspaces, isPlaceholder, plan]);

  const { workspace, isResolved } = useMemo(
    () => resolveActiveWorkspace({ slugInUrl, workspaces, isPlaceholder }),
    [slugInUrl, workspaces, isPlaceholder],
  );

  const setWorkspace = useCallback(
    (id: string) => {
      if (isResolved && workspace.id === id) return;
      setPendingWorkspaceId(id);
    },
    [isResolved, workspace.id],
  );

  useEffect(() => {
    if (
      shouldFinishWorkspaceSwitch({
        pendingWorkspaceId,
        resolvedWorkspaceId: workspace.id,
        isResolved,
      })
    ) {
      setPendingWorkspaceId(null);
      return;
    }

    if (!pendingWorkspaceId) return;

    const target = workspaces.find((candidate) => candidate.id === pendingWorkspaceId);
    if (!target) return;

    const targetPath = buildWorkspaceSwitchPath({
      pathname: location.pathname,
      currentSlug: slugInUrl,
      targetSlug: target.slug,
    });

    if (location.pathname !== targetPath) {
      void navigate({ to: targetPath });
    }
  }, [
    pendingWorkspaceId,
    workspace.id,
    isResolved,
    workspaces,
    location.pathname,
    slugInUrl,
    navigate,
  ]);

  const value = useMemo<ActiveWorkspaceContextValue>(
    () => ({
      workspace,
      isResolved,
      workspaces,
      setWorkspace,
      isSwitching: pendingWorkspaceId !== null,
    }),
    [workspace, isResolved, workspaces, setWorkspace, pendingWorkspaceId],
  );

  return <ActiveWorkspaceContext value={value}>{children}</ActiveWorkspaceContext>;
}

export function resolveActiveWorkspace({
  slugInUrl,
  workspaces,
  isPlaceholder = false,
}: {
  slugInUrl?: string;
  workspaces: ReadonlyArray<Workspace>;
  /** True when `workspaces` is the mock fallback rather than the caller's real list. */
  isPlaceholder?: boolean;
}): { workspace: Workspace; isResolved: boolean } {
  // A mock workspace id is not a real one: consumers gate network requests on
  // `isResolved`, and the mock id 404s on every endpoint.
  if (isPlaceholder) {
    return { workspace: workspaces[0] ?? DEFAULT_MOCK_WORKSPACE, isResolved: false };
  }

  if (slugInUrl) {
    const bySlug = workspaces.find((workspace) => workspace.slug === slugInUrl);
    if (bySlug) {
      return { workspace: bySlug, isResolved: true };
    }
  }

  return {
    workspace:
      workspaces.find((workspace) => !workspace.isDemo) ?? workspaces[0] ?? DEFAULT_MOCK_WORKSPACE,
    isResolved: slugInUrl == null,
  };
}

export function useActiveWorkspace(): ActiveWorkspaceContextValue {
  const context = use(ActiveWorkspaceContext);
  if (context === null) {
    throw new Error("useActiveWorkspace must be used inside an <ActiveWorkspaceProvider>");
  }
  return context;
}
